const NAV_SELECTOR = 'nav[header-menu]';
const LINK_SELECTOR = '.menu-list__link';
const ACTIVE_LINK_SELECTOR = '.menu-list__link--active';
const LINE_WIDTH_RATIO = 0.72;
const LINE_MIN_WIDTH = 18;
const LINE_HEIGHT = 1;
const CLICK_ANIMATION_DURATION = 320;

const instances = new WeakMap();

class HeaderNavPill {
  /**
   * @param {HTMLElement} nav
   */
  constructor(nav) {
    this.nav = nav;
    this.menu = nav.querySelector('.menu-list');
    this.pill = nav.querySelector('.menu-list__pill');
    this.abortController = new AbortController();
    this.currentLink = null;
    this.frame = 0;
    this.lastX = 0;
    this.clickTimeout = 0;

    if (!(this.menu instanceof HTMLElement) || !(this.pill instanceof HTMLElement)) return;

    const signal = this.abortController.signal;

    this.nav.addEventListener('pointerover', this.handlePointerOver, { signal });
    this.nav.addEventListener('pointerleave', this.handlePointerLeave, { signal });
    this.nav.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.nav.addEventListener('focusin', this.handleFocusIn, { signal });
    this.nav.addEventListener('focusout', this.handleFocusOut, { signal });
    window.addEventListener('resize', this.updateCurrent, { signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateCurrent);
      this.resizeObserver.observe(this.menu);
    }

    this.mutationObserver = new MutationObserver(this.updateCurrent);
    this.mutationObserver.observe(this.menu, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'slot'],
      childList: true,
      subtree: true,
    });

    document.fonts?.ready?.then(this.updateCurrent).catch(() => {});

    this.updateCurrent();
  }

  destroy() {
    this.abortController.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.clickTimeout) clearTimeout(this.clickTimeout);
  }

  handlePointerOver = (event) => {
    const link = this.getUsableLink(event.target);
    if (link) this.moveTo(link, 'hover');
  };

  handlePointerLeave = () => {
    this.restOnActiveLink();
  };

  handlePointerDown = (event) => {
    const link = this.getUsableLink(event.target);
    if (!link) return;

    this.moveTo(link, 'hover');
    this.nav.dataset.pillClicking = 'false';

    requestAnimationFrame(() => {
      this.nav.dataset.pillClicking = 'true';
    });

    if (this.clickTimeout) clearTimeout(this.clickTimeout);
    this.clickTimeout = setTimeout(() => {
      this.nav.dataset.pillClicking = 'false';
    }, CLICK_ANIMATION_DURATION);
  };

  handleFocusIn = (event) => {
    const link = this.getUsableLink(event.target);
    if (link) this.moveTo(link, 'hover');
  };

  handleFocusOut = () => {
    requestAnimationFrame(() => {
      if (!this.nav.contains(document.activeElement)) this.restOnActiveLink();
    });
  };

  updateCurrent = () => {
    if (this.frame) cancelAnimationFrame(this.frame);

    this.frame = requestAnimationFrame(() => {
      this.frame = 0;

      if (this.currentLink && this.isUsableLink(this.currentLink)) {
        this.moveTo(this.currentLink, this.currentLink.matches(ACTIVE_LINK_SELECTOR) ? 'active' : 'hover');
        return;
      }

      this.restOnActiveLink();
    });
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  getUsableLink(target) {
    if (!(target instanceof Element)) return null;

    const link = target.closest(LINK_SELECTOR);
    return link instanceof HTMLElement && this.isUsableLink(link) ? link : null;
  }

  /**
   * @param {HTMLElement} link
   * @returns {boolean}
   */
  isUsableLink(link) {
    if (!this.nav.contains(link)) return false;
    if (link.closest('.menu-list__submenu')) return false;

    const item = link.closest('.menu-list__list-item');
    if (!(item instanceof HTMLElement)) return false;
    if (item.getAttribute('slot') === 'overflow' || item.getAttribute('slot') === 'more') return false;
    if (item.getAttribute('aria-hidden') === 'true') return false;

    const rect = link.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * @param {HTMLElement} link
   * @param {'active' | 'hover'} state
   */
  moveTo(link, state) {
    if (!this.menu) return;

    const linkRect = link.getBoundingClientRect();
    const menuRect = this.menu.getBoundingClientRect();
    const title = link.querySelector('.menu-list__link-title');
    const targetRect = title instanceof HTMLElement ? title.getBoundingClientRect() : linkRect;
    const width = Math.max(LINE_MIN_WIDTH, targetRect.width * LINE_WIDTH_RATIO);
    const x = targetRect.left - menuRect.left + (targetRect.width - width) / 2;
    const y = linkRect.bottom - menuRect.top - 5;
    const direction = x > this.lastX ? 'right' : x < this.lastX ? 'left' : 'none';

    this.currentLink = link;
    this.lastX = x;
    this.menu.style.setProperty('--header-nav-pill-x', `${x}px`);
    this.menu.style.setProperty('--header-nav-pill-y', `${y}px`);
    this.menu.style.setProperty('--header-nav-pill-width', `${width}px`);
    this.menu.style.setProperty('--header-nav-pill-height', `${LINE_HEIGHT}px`);
    this.nav.dataset.pillVisible = 'true';
    this.nav.dataset.pillState = state;
    this.nav.dataset.pillDirection = direction;
  }

  restOnActiveLink() {
    const activeLink = this.getActiveLink();

    if (activeLink) {
      this.moveTo(activeLink, 'active');
      return;
    }

    this.currentLink = null;
    this.nav.dataset.pillVisible = 'false';
    this.nav.dataset.pillState = 'idle';
  }

  /**
   * @returns {HTMLElement | null}
   */
  getActiveLink() {
    const activeLinks = this.nav.querySelectorAll(ACTIVE_LINK_SELECTOR);

    for (const link of activeLinks) {
      if (link instanceof HTMLElement && this.isUsableLink(link)) return link;
    }

    return null;
  }
}

function initHeaderNavPills() {
  document.querySelectorAll(NAV_SELECTOR).forEach((nav) => {
    if (!(nav instanceof HTMLElement) || instances.has(nav)) return;

    const instance = new HeaderNavPill(nav);
    instances.set(nav, instance);
  });
}

function destroyHeaderNavPills(event) {
  if (!(event.target instanceof Element)) return;

  event.target.querySelectorAll(NAV_SELECTOR).forEach((nav) => {
    const instance = instances.get(nav);
    if (!instance) return;

    instance.destroy();
    instances.delete(nav);
  });
}

document.addEventListener('shopify:section:load', initHeaderNavPills);
document.addEventListener('shopify:section:unload', destroyHeaderNavPills);
initHeaderNavPills();
